import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/PublicPages.css';

const PrivacyPolicy = () => (
    <div className="public-page">
        <header className="public-header">
            <Link to="/" className="public-brand">
                <img src="/GTS.png" alt="GTS Portal" />
                <span>GTS Portal</span>
            </Link>
            <Link to="/login" className="public-header-link">Sign in</Link>
        </header>

        <main className="public-content">
            <p className="public-eyebrow">GTS Portal</p>
            <h1>Privacy Policy</h1>
            <p className="public-updated">Last updated: October 1, 2026</p>

            <p>
                GTS Portal is an internal workplace management platform operated for organizations
                and their employees. This Privacy Policy explains what information the platform
                handles, why it is used, and the choices available to account holders.
            </p>

            <h2>Information we handle</h2>
            <p>Depending on your organization and role, GTS Portal may handle:</p>
            <ul>
                <li>Account details such as your name, email address, employee ID, role and profile photo.</li>
                <li>Employment information such as department, designation, work location, shift and reporting relationships.</li>
                <li>Attendance, leave, work-from-home, task, project, expense, payroll and inventory records.</li>
                <li>Documents, acknowledgements, messages, comments and files that you or your organization submit.</li>
                <li>Technical information needed to operate and secure the service, such as IP address, browser or device details, logs and notification status.</li>
                <li>Voice notes, audio, video and other media when those features are used.</li>
            </ul>

            <h2>How we use information</h2>
            <p>Information is used to provide and administer the platform, including to:</p>
            <ul>
                <li>Authenticate users and enforce role-based access.</li>
                <li>Record attendance and manage workplace requests, tasks, projects, expenses and payroll.</li>
                <li>Deliver notifications, messages, documents and organization communications.</li>
                <li>Maintain security, prevent misuse, troubleshoot issues and improve reliability.</li>
                <li>Meet legal, accounting, employment and audit obligations.</li>
            </ul>

            <h2>Who can access information</h2>
            <p>
                Information is available to the account holder and authorized people within their
                organization according to role and permissions. We may also use service providers
                for hosting, cloud file storage, email delivery, communications, analytics or
                speech and assistant features. Providers receive only the information needed for
                the relevant service and are expected to protect it.
            </p>

            <h2>Optional web analytics</h2>
            <p>
                If enabled by your organization, the web portal offers Microsoft Clarity to help us
                understand how people use the site. If you accept analytics, Clarity may collect page
                visits, clicks, scrolling, browser and device information, and session replay data.
                It may use analytics cookies. You can decline without losing access to the portal
                and change your choice using the Analytics preferences control on the sign-in page.
                Clarity is not loaded unless you accept. Learn more in the{' '}
                <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer">
                    Microsoft Privacy Statement
                </a>.
            </p>

            <h2>Mobile app</h2>
            <p>
                The GTS Portal app for iPhone and Android gives you the same account and information
                as the web portal. It also uses some features of your device, each only when you allow it
                and only for the purpose described:
            </p>
            <ul>
                <li>
                    <strong>Microphone</strong> — voice calls with colleagues, recording voice notes, and
                    speaking a task assignment.
                </li>
                <li>
                    <strong>Speech recognition</strong> — turning a spoken task briefing into text. This is
                    done by your device&apos;s speech service (Apple on iPhone, Google on Android). Only the
                    resulting text is sent to GTS Portal, where it is turned into editable task drafts, which
                    may involve an AI service provider. The audio itself is not uploaded.
                </li>
                <li>
                    <strong>Camera and photos</strong> — taking or choosing photos and videos to share in chats
                    and tasks, and setting your profile photo. Only the items you choose are uploaded.
                </li>
                <li>
                    <strong>Notifications</strong> — alerting you to messages, task updates, approvals and
                    incoming calls. To deliver them, the app registers a notification token for your device
                    with Apple Push Notification service (iPhone) or Firebase Cloud Messaging (Android). The
                    token is stored against your account and removed when you sign out. Notifications carry
                    only what is needed to show them, such as the sender&apos;s name and a short preview.
                </li>
            </ul>
            <p>
                <strong>Voice calls</strong> are connected directly between participants&apos; devices where
                possible, or relayed through our own call server when a direct connection is not possible.
                Call audio is encrypted in transit and is not recorded or stored. A call history (who took
                part, when, and for how long) is kept with the conversation. On iPhone, incoming calls use
                Apple&apos;s CallKit, so they appear on the standard call screen; calls are not added to your
                phone&apos;s recent calls list.
            </p>
            <p>
                The app keeps your sign-in session in your device&apos;s secure storage, and a small, temporary
                cache of list summaries (such as your chat list, recent calls and dashboard) so it opens quickly.
                Message contents and media are not kept in that cache. Both are removed when you sign out or
                uninstall the app. The mobile app does not include advertising or third-party tracking.
            </p>

            <h2>Retention</h2>
            <p>
                Account and workplace information is retained while an account or organization
                relationship is active. Some attendance, payroll, expense, document acknowledgement,
                communication and audit records may be retained after account closure when needed
                for legal, accounting, security or legitimate business purposes. Personal account
                information that is no longer required is deleted or anonymized according to the
                organization&apos;s retention practices.
            </p>

            <h2>Security</h2>
            <p>
                GTS Portal uses authentication, access controls and technical safeguards designed
                to protect information. No internet service can guarantee absolute security, so
                keep your password confidential and report suspected unauthorized access to your
                organization&apos;s administrator.
            </p>

            <h2>Your choices and requests</h2>
            <p>
                Employees can request access, correction or deletion of their account information
                through their organization&apos;s HR team or portal administrator. See our{' '}
                <Link to="/delete-account">Account Deletion page</Link> for the deletion request process.
            </p>

            <h2>Children&apos;s privacy</h2>
            <p>
                GTS Portal is a workplace service and is not intended for children.
            </p>

            <h2>Changes to this policy</h2>
            <p>
                We may update this policy when the platform, law or processing practices change.
                The updated version will be published on this page with a revised date.
            </p>

            <h2>Contact</h2>
            <p>
                For privacy questions or requests, contact your organization&apos;s HR team or portal
                administrator through the official communication channel provided by your employer.
            </p>
        </main>

        <footer className="public-footer">
            <span>GTS Portal — internal use only</span>
            <nav aria-label="Legal links">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/delete-account">Delete Account</Link>
                <Link to="/">Home</Link>
            </nav>
        </footer>
    </div>
);

export default PrivacyPolicy;
